/**
 * Turns the model's free text into editable calendar rows, and those rows into the instants the
 * confirm endpoint wants.
 *
 * <p>The extraction reads what is printed on a schedule — "3/14", "Sat, March 14", "5:30 PM" — and
 * deliberately does not resolve it. Resolving needs a timezone and, for a date with no year, a
 * sense of "now"; the browser is the only participant that reliably knows either. So this module is
 * where a photographed schedule becomes something a calendar can hold.</p>
 *
 * <p>Pure on purpose: `today` and the timezone are arguments, never `new Date()` or
 * `Intl` reads, so every rule below is testable without freezing the clock.</p>
 */

import { browserTimeZone, zonedInputToUtcIso } from "../../../../utils/calendarTime";
import type {
  BulkCalendarEventDto,
  ConfirmBulkCalendarEventDto,
} from "../../../../types/schedule-ingestion";

/** Mirrors BulkCalendarEventConfirmationService.MaxEventsPerConfirm. */
export const MAX_BULK_EVENTS = 100;

/** Mirrors the calendar_events column widths, and the server's own checks. */
export const MAX_TITLE_LENGTH = 200;
export const MAX_LOCATION_LENGTH = 300;

const MS_PER_DAY = 86_400_000;
const MS_PER_HOUR = 3_600_000;

/** What a row with no printed time becomes, when the coach unticks "all day". */
export const DEFAULT_START_TIME = "09:00";

/** One editable row in the review list. */
export interface BulkEventDraft {
  /** Stable across edits, so React never reorders inputs under the coach's cursor. */
  key: string;
  /** Unticked rows are left behind — the coach's way of dropping a row the model invented. */
  include: boolean;
  title: string;
  /** Wall-clock date in the coach's zone, `YYYY-MM-DD`. Empty when the schedule gave none. */
  date: string;
  /** Wall-clock `HH:mm`, or empty. Ignored while `allDay`. */
  startTime: string;
  endTime: string;
  /**
   * The day the event ENDS, `YYYY-MM-DD`. Usually the same as `date`; different for a game that runs
   * past midnight. Explicit rather than inferred, because `DateTimePicker` is a date-AND-time
   * control — inferring it meant the coach could pick a next-day end and watch the field snap back.
   */
  endDate: string;
  allDay: boolean;
  location: string;
  /**
   * The row arrived incomplete — the server flagged it, we could not read its date, or a time WAS
   * printed and we could not read it. Surfaced as a "check this" badge rather than as a blocker:
   * the coach fixes it in place.
   */
  needsReview: boolean;
}

/** Recomputed after every edit, so settling one field cannot clear a badge another field earned. */
export function draftNeedsReview(draft: BulkEventDraft): boolean {
  return draft.title.trim() === "" || draft.date === "";
}

const pad = (n: number) => String(n).padStart(2, "0");

const MONTHS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

/** Weekday prefixes the model copies off the schedule ("Sat 3/14", "Saturday, March 14"). */
const WEEKDAY_PREFIX = /^(sun|mon|tue|wed|thu|fri|sat)[a-z]*\.?,?\s+/i;

/**
 * Years outside this band are not a schedule, they are a misread. The bound matters more than it
 * looks: `isoDate` pads the month and day but a 3-digit year stays 3 digits, and
 * `zonedInputToUtcIso("202-03-14T09:00")` gets an Invalid Date whose fallback is
 * `new Date().toISOString()` — so an OCR-truncated "3/14/202" would create the event silently at
 * the moment of confirm, on a row that looked fine in review.
 */
const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

const isoDate = (year: number, month: number, day: number) =>
  `${year}-${pad(month)}-${pad(day)}`;

/** Does this day exist? Guards "2/30" and "4/31", which a Date constructor would silently roll. */
const isRealDay = (year: number, month: number, day: number) => {
  if (year < MIN_YEAR || year > MAX_YEAR) return false;
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const probe = new Date(Date.UTC(year, month - 1, day));
  return probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
};

/**
 * Choose a year for a date that was printed without one.
 *
 * <p>The nearest of last year, this year and next year. A season sheet is almost always upcoming,
 * so "this year" is usually right — but a January upload of a schedule that runs through the
 * holidays, or a March upload of last November's results, both land where a coach would expect
 * rather than a year out.</p>
 */
const inferYear = (month: number, day: number, today: Date): number => {
  const todayMs = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const thisYear = today.getFullYear();
  let best = thisYear;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const year of [thisYear - 1, thisYear, thisYear + 1]) {
    if (!isRealDay(year, month, day)) continue;
    const distance = Math.abs(Date.UTC(year, month - 1, day) - todayMs);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = year;
    }
  }
  return best;
};

/**
 * Read a date the way it was printed. Returns `YYYY-MM-DD`, or the empty string when there is
 * nothing to read — never a guessed date, because a wrong date that looks confident is worse on a
 * calendar than a blank the coach is asked to fill.
 */
export function parseScheduleDate(raw: string | null | undefined, today: Date): string {
  if (!raw) return "";
  let text = raw.trim().replace(WEEKDAY_PREFIX, "").trim();
  if (!text || text === "-") return "";
  // "March 14th" / "1st of June" — the suffix carries no information we need.
  text = text.replace(/(\d)(st|nd|rd|th)\b/gi, "$1");

  // ISO first: it is what the prompt asks for, and it is unambiguous.
  const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    const [, y, m, d] = iso;
    return isRealDay(+y, +m, +d) ? isoDate(+y, +m, +d) : "";
  }

  // Numeric: M/D, M/D/YY, M/D/YYYY, and the same with dashes or dots.
  const numeric = text.match(/^(\d{1,2})[/\-.](\d{1,2})(?:[/\-.](\d{2}|\d{4}))?$/);
  if (numeric) {
    const month = +numeric[1];
    const day = +numeric[2];
    if (!numeric[3]) {
      const year = inferYear(month, day, today);
      return isRealDay(year, month, day) ? isoDate(year, month, day) : "";
    }
    // A two-digit year is this century. Schedules are not historical documents.
    const year = numeric[3].length === 2 ? 2000 + +numeric[3] : +numeric[3];
    return isRealDay(year, month, day) ? isoDate(year, month, day) : "";
  }

  // Named month, either order: "March 14, 2026", "Mar 14", "14 March 2026".
  const monthFirst = text.match(/^([a-z]+)\.?\s+(\d{1,2})(?:,?\s*(\d{4}))?$/i);
  const dayFirst = text.match(/^(\d{1,2})\s+([a-z]+)\.?(?:,?\s*(\d{4}))?$/i);
  const named = monthFirst
    ? { name: monthFirst[1], day: monthFirst[2], year: monthFirst[3] }
    : dayFirst
      ? { name: dayFirst[2], day: dayFirst[1], year: dayFirst[3] }
      : null;
  if (named) {
    const name = named.name.toLowerCase();
    // >= 3 characters, so "ma" does not silently resolve to March over May.
    const monthIndex =
      name.length >= 3 ? MONTHS.findIndex((m) => m.startsWith(name)) : -1;
    if (monthIndex >= 0) {
      const month = monthIndex + 1;
      const day = +named.day;
      const year = named.year ? +named.year : inferYear(month, day, today);
      return isRealDay(year, month, day) ? isoDate(year, month, day) : "";
    }
  }

  return "";
}

/**
 * Read a time the way it was printed. Returns `HH:mm` (24-hour) or the empty string.
 *
 * <p>Empty is a real answer, not a failure: plenty of schedule rows carry no time, and inventing
 * one would put an event on the calendar at an hour nobody wrote down.</p>
 */
export function parseScheduleTime(raw: string | null | undefined): string {
  if (!raw) return "";
  // Only the meridiem's dots. A global strip turned "5.30 PM" into "530 pm", which then matched
  // nothing and silently became an all-day event.
  const text = raw.trim().toLowerCase().replace(/([ap])\.\s*m\.?/g, "$1m").trim();
  if (!text || text === "-") return "";
  if (text === "noon") return "12:00";
  if (text === "midnight") return "00:00";

  // `.` and `h` separate an hour from its minutes on plenty of printed schedules; four bare digits
  // are military time. Both were previously read as nothing.
  const match =
    text.match(/^(\d{1,2})(?:[:.h](\d{2}))?\s*(am|pm|a|p)?$/) ??
    text.match(/^(\d{2})(\d{2})()$/);
  if (!match) return "";

  let hour = +match[1];
  const minute = match[2] ? +match[2] : 0;
  const meridiem = match[3]?.[0];

  if (minute > 59) return "";

  if (meridiem) {
    if (hour < 1 || hour > 12) return "";
    hour = hour % 12;
    if (meridiem === "p") hour += 12;
  } else if (hour > 23) {
    return "";
  }

  return `${pad(hour)}:${pad(minute)}`;
}

/**
 * Build the editable review list from what the model extracted.
 *
 * <p>Every row is kept and pre-ticked, including the ones flagged for review. Silently dropping a
 * half-read row is the one failure a coach cannot detect — they would have to re-read the original
 * document to notice an event never arrived.</p>
 */
export function toDrafts(events: BulkCalendarEventDto[], today: Date): BulkEventDraft[] {
  return events.map((event, index) => {
    const date = parseScheduleDate(event.date, today);
    const startTime = parseScheduleTime(event.startTime);
    // Shown as an hour after the start when the schedule printed no end, because that is what the
    // payload builder writes. Leaving it blank made the Ends field read identically to Starts, so
    // the coach approved a zero-length event and got a one-hour one.
    const endTime = parseScheduleTime(event.endTime) || plusAnHour(startTime);
    // A printed time we could not read is a different thing from no printed time. The first is a
    // dropped value the coach must be told about; the second is an all-day event.
    const timeWasDropped =
      (event.startTime ?? "").trim() !== "" && (event.startTime ?? "").trim() !== "-" &&
      startTime === "";
    return {
      key: `row-${index}`,
      include: true,
      title: (event.title ?? "").trim(),
      date,
      startTime,
      endTime,
      endDate: date,
      // No printed time means an all-day event, which is what "Team photo day" actually is. The
      // alternative — defaulting to 9am — puts an event on the calendar at an hour the document
      // never stated.
      allDay: startTime === "",
      location: (event.location ?? "").trim(),
      needsReview: event.needsReview || date === "" || timeWasDropped,
    };
  });
}

/** `HH:mm` an hour later, wrapping past midnight. Blank in, blank out. */
function plusAnHour(time: string): string {
  if (!time) return "";
  const hour = (+time.slice(0, 2) + 1) % 24;
  return `${pad(hour)}:${time.slice(3)}`;
}

/**
 * Compose an event's final name from the row and the team the schedule belongs to.
 *
 * <p>A screenshot of a printed schedule very often loses the header — the row says "vs Central"
 * or just "Central", and whose schedule it is was in the part that got cut off. Without that, the
 * event on the calendar names one team and nobody can tell which side of it the program is on.
 * So the coach names the team once and every event carries it.</p>
 *
 * <p>Nothing is inferred beyond joining the two, and a title that already names the team is left
 * alone — re-running this over an edited row must not produce "Lincoln HS Lincoln HS vs Central".
 * Blank team in, title out unchanged, which is the whole behaviour when the field is left empty.</p>
 */
export function composeEventTitle(title: string, team: string): string {
  const rowTitle = title.trim();
  const teamName = team.trim();
  if (!teamName) return rowTitle;
  if (!rowTitle) return teamName;

  // Case-insensitive: a coach who typed "lincoln hs" into a row means the same team as the field.
  if (rowTitle.toLowerCase().includes(teamName.toLowerCase())) return rowTitle;

  // An opponent, printed the way schedules print one. "Lincoln HS vs Central" needs no separator
  // because the row already supplies the connecting word.
  if (/^(vs\.?|v\.?|@|at)\s/i.test(rowTitle)) return `${teamName} ${rowTitle}`;

  // Anything else is a named event rather than a fixture — "Team photo day", "Media day".
  return `${teamName} - ${rowTitle}`;
}

/**
 * The day after a "YYYY-MM-DD", as another "YYYY-MM-DD".
 *
 * Via UTC on purpose: this is calendar arithmetic on a bare date, with no zone and no clock in
 * it, so going through a local Date would let the host"s own offset move the answer.
 */
const nextDay = (date: string): string => {
  const next = new Date(`${date}T00:00:00.000Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
};

/** Rows the coach kept. */
export const includedDrafts = (drafts: BulkEventDraft[]) => drafts.filter((d) => d.include);

/**
 * Why the confirm button is disabled, or the empty string when it is not.
 *
 * <p>One message rather than a list: the rows themselves carry their own inline state, so a summary
 * at the button only has to say what is standing in the way right now.</p>
 */
export function confirmBlockedReason(drafts: BulkEventDraft[]): string {
  const included = includedDrafts(drafts);
  if (included.length === 0) return "Tick at least one event to add.";
  if (included.length > MAX_BULK_EVENTS) {
    return `That is more than ${MAX_BULK_EVENTS} events. Untick some, or split the schedule into parts.`;
  }
  if (included.some((d) => d.title.trim() === "")) return "Every event needs a name.";
  if (included.some((d) => d.date === "")) return "Every event needs a date.";
  // Mirrors the calendar_events column widths the server enforces. Caught here so one over-long row
  // is named while the coach is looking at it, rather than failing the whole all-or-nothing batch.
  if (included.some((d) => d.title.trim().length > MAX_TITLE_LENGTH)) {
    return `An event name must be ${MAX_TITLE_LENGTH} characters or fewer.`;
  }
  if (included.some((d) => d.location.trim().length > MAX_LOCATION_LENGTH)) {
    return `A location must be ${MAX_LOCATION_LENGTH} characters or fewer.`;
  }
  return "";
}

/**
 * Resolve the kept rows into the instants the API stores.
 *
 * <p>All-day events are UTC midnight to exclusive UTC midnight, matching what the event editor
 * writes; a timed event is its wall clock read in `timeZone`. Getting these two frames confused is
 * a real bug that has shipped in this codebase before, which is why they are computed here once
 * rather than at each call site.</p>
 */
export function toConfirmPayload(
  drafts: BulkEventDraft[],
  timeZone: string = browserTimeZone(),
): ConfirmBulkCalendarEventDto[] {
  return includedDrafts(drafts)
    .filter((draft) => draft.date !== "" && draft.title.trim() !== "")
    .map((draft) => {
      const title = draft.title.trim();
      const locationName = draft.location.trim() || undefined;

      if (draft.allDay) {
        const start = `${draft.date}T00:00:00.000Z`;
        return {
          title,
          startsAtUtc: start,
          endsAtUtc: new Date(new Date(start).getTime() + MS_PER_DAY).toISOString(),
          isAllDay: true,
          locationName,
        };
      }

      const startTime = draft.startTime || DEFAULT_START_TIME;
      const startsAtUtc = zonedInputToUtcIso(`${draft.date}T${startTime}`, timeZone);
      const startMs = new Date(startsAtUtc).getTime();

      /*
        The overnight roll, decided on the DATE rather than on an instant.

        An end at or before the start on the same day means the event runs past midnight —
        "10:00 PM | 1:00 AM" is one late game printed on one line, not a negative-length one. When
        the coach set a different end DATE they said what they meant, so nothing is inferred.

        It has to be the date now, because the wall clock below is what the server resolves
        against the venue"s zone: rolling an instant by 24h would leave the local string saying
        the wrong day.
      */
      const sameDay = (draft.endDate || draft.date) === draft.date;
      const endTime = draft.endTime || plusAnHour(startTime);
      const rollsOvernight = sameDay && endTime <= startTime;
      const endDate = rollsOvernight ? nextDay(draft.date) : draft.endDate || draft.date;

      const endMs = draft.endTime
        ? new Date(zonedInputToUtcIso(`${endDate}T${endTime}`, timeZone)).getTime()
        : startMs + MS_PER_HOUR;

      return {
        title,
        /*
          Both frames are sent. The wall clock is what the server prefers, because it can resolve
          it against the VENUE"s timezone — a schedule printed for a tournament in Arizona reads
          5:30 PM because that is Arizona time, whatever zone the coach"s laptop is in.

          The instants stay for a server that predates that: the two deploy separately, and an
          older API reads these and ignores the strings.
        */
        startsAtLocal: `${draft.date}T${startTime}`,
        endsAtLocal: `${endDate}T${endTime}`,
        startsAtUtc,
        endsAtUtc: new Date(endMs).toISOString(),
        isAllDay: false,
        locationName,
      };
    });
}
